<script setup lang="ts">
import type { JSX } from '@ionic/core'
import type { AssociationMembershipResponse } from '~~/openapi-client/gen/schemas'

defineProps<JSX.IonItem & {
	member: AssociationMembershipResponse
	description?: string
	errorText?: string
	selected?: boolean
	selectable?: boolean
	hideSelect?: boolean
}>()

defineEmits(['click', 'select'])
</script>

<template>
	<ion-item
		v-bind.attr="$attrs"
		v-bind.prop="$props"
		:class="{ invalid: !!errorText }"
		@click="$emit('click')"
	>
		<ion-avatar>
			<i
				:class="['mdi', { 'mdi-account': !errorText, 'mdi-alert-outline': !!errorText }]"
				style="font-size: 18px; color: #FFFFFF;"
			/>
		</ion-avatar>
		<ion-label :class="{ shake: !!errorText }">
			<h2>{{ member.lastname }}, {{ member.firstname }}</h2>
			<template v-if="description">
				<p>{{ description }}</p>
			</template>
			<template v-else>
				<p>{{ member.tags.map(tag => tag.name).join(', ') }}</p>
			</template>
		</ion-label>
		<ion-buttons
			v-if="$slots.append || !hideSelect"
			slot="end"
		>
			<slot
				v-if="$slots.append"
				name="append"
			/>
			<ion-checkbox
				v-if="!hideSelect"
				:checked="selected"
				:disabled="!selectable"
				:aria-label="`Mitglied ${member.firstname} ${member.lastname} auswählen`"
				@ion-change="$emit('select', $event.detail.checked)"
			/>
		</ion-buttons>
	</ion-item>
	<ion-row
		v-if="!!errorText"
		class="error-text"
	>
		<ion-col size="auto">
			<ion-text>
				<small>{{ errorText }}</small>
			</ion-text>
		</ion-col>
	</ion-row>
</template>

<style lang="scss" scoped>
ion-item {
	--border-width: 1px 0 1px 0 !important;
	--border-color: var(--ion-color-grey-5);
	--min-height: 64px;

	&:deep(ion-label) {
		h2 {
			font-size: 14px;
			font-weight: 600;
		}

		p {
			font-size: 12px;
			font-weight: 400;
			color: var(--ion-color-secondary);
		}
	}
}

ion-avatar {
	background: var(--ion-color-secondary);
	position: relative;
	margin-right: 12px;

	i {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
}

ion-checkbox {
	--border-color: var(--ion-color-primary-contrast);
	--border: 2px;

	&.checkbox-checked {
		--checkbox-background-checked: var(--ion-color-primary);
		--border-color-checked: var(--ion-color-primary-contrast);
	}

	&::part(mark) {
		display: none;
	}
}

.invalid {
	--border-color: var(--ion-color-secondary);
	--background: var(--ion-color-danger);
	--highlight-color: var(--ion-color-primary-contrast);
	--highlight-color-invalid: var(--ion-color-primary-contrast);
}

.error-text {
	padding: 0 16px;
	justify-content: center;
}

@keyframes shake {
	0% { transform: translateX(0); }
	20% { transform: translateX(-6px); }
	40% { transform: translateX(6px); }
	60% { transform: translateX(-6px); }
	80% { transform: translateX(6px); }
	100% { transform: translateX(0); }
}

.shake {
	animation: shake 0.4s;
}
</style>
